import { useState, useEffect, useRef } from 'react';
import { getQuote, createQuote, updateQuote, sendQuote, type QuoteDetail } from '@/lib/api/quotes';
import apiClient from '@/lib/api/client';
import { PrintableQuote } from '@/components/estimates/PrintableQuote';
import { printQuote } from '@/lib/utils/generateInvoicePdf';
import { getSettings } from '@/lib/api/settings';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

interface LineItem {
  id: string;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  unit: string;
}

interface Section {
  id: string;
  title: string;
  items: LineItem[];
}

interface Props {
  jobId: string;
  quoteId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export const QuoteBuilderModal = ({ jobId, quoteId: initialQuoteId, onClose, onSaved }: Props) => {
  const printableRef = useRef<HTMLDivElement>(null);
  const [quoteId, setQuoteId] = useState<string | null>(initialQuoteId ?? null);
  const [quoteData, setQuoteData] = useState<QuoteDetail | null>(null);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [status, setStatus] = useState('Draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxRate, setTaxRate] = useState(5); // BC: GST only, matches invoicing
  const [sections, setSections] = useState<Section[]>([
    { id: `s-${Date.now()}`, title: 'Section 1', items: [] },
  ]);
  const [companyInfo, setCompanyInfo] = useState<{
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  } | undefined>(undefined);
  const [actionError, setActionError] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [showNewSectionInput, setShowNewSectionInput] = useState(false);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState<string | null>(null);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  // Fetch company settings for printing
  useEffect(() => {
    getSettings()
      .then((settings) => {
        if (settings.company) {
          const c = settings.company;
          const addressParts = [
            c.address,
            c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state,
            c.zipCode,
          ].filter(Boolean);

          setCompanyInfo({
            name: c.companyName || 'OchrePoint Contracting',
            address: addressParts.join('\n'),
            phone: c.phone || '',
            email: c.email || '',
            logo: c.logoUrl,
          });
        }
      })
      .catch((err) => {
        console.error('Failed to load company settings:', err);
      });
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (initialQuoteId) {
      loadQuote(initialQuoteId);
    } else {
      createQuote(jobId)
        .then(({ id, quoteNumber: qn }) => {
          setQuoteId(String(id));
          setQuoteNumber(qn);
          setLoading(false);
          loadQuote(String(id)); // Load full details
        })
        .catch(() => { setActionError('Failed to create quote.'); setLoading(false); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuote = async (id: string) => {
    try {
      setLoading(true);
      const data = await getQuote(id);
      setQuoteData(data);
      const q = data;
      setQuoteNumber(q.quote_number ?? '');
      setJobTitle(q.job_title ?? '');
      setClientName(q.client_name ?? '');
      setStatus(q.status ?? 'Draft');
      if (q.tax_rate != null) setTaxRate(Number(q.tax_rate)); // 0 is a real rate

      const rawSections: any[] = q.sections ?? [];
      const rawItems: any[] = q.items ?? [];
      if (rawSections.length > 0) {
        setSections(rawSections.map((s: any) => ({
          id: String(s.id),
          title: s.name ?? s.title ?? 'Section',
          items: rawItems
            .filter((i: any) => String(i.section_id) === String(s.id))
            .map((i: any) => ({
              id: String(i.id),
              name: i.item_name ?? i.name ?? '',
              description: i.description ?? '',
              quantity: Number(i.quantity) || 1,
              unitPrice: Number(i.unit_price) || 0,
              unit: i.unit ?? 'ea',
            })),
        })));
      }
    } catch {
      setActionError('Failed to load quote.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printableRef.current) return;
    printQuote(printableRef.current);
  };

  const subtotal = sections.reduce(
    (t, s) => t + s.items.reduce((st, i) => st + i.quantity * i.unitPrice, 0),
    0,
  );
  const tax = (subtotal * taxRate) / 100;
  const grandTotal = subtotal + tax;

  const buildPayload = () =>
    sections.map((s, si) => ({
      name: s.title,
      display_order: si,
      items: s.items.map((i, ii) => ({
        item_name: i.name || 'Unnamed',
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unitPrice,
        unit: i.unit,
        display_order: ii,
      })),
    }));

  const handleSave = async () => {
    if (!quoteId) return;
    setActionError('');
    try {
      setSaving(true);
      await apiClient.put(`/quotes/${quoteId}/sections`, { sections: buildPayload(), tax_rate: taxRate });
      await updateQuote(quoteId, { status: 'draft' });
      onSaved();
      onClose();
    } catch {
      setActionError('Failed to save quote.');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    setConfirmSendOpen(true);
  };

  const confirmSend = async () => {
    if (!quoteId) return;
    setConfirmSendOpen(false);
    setActionError('');
    try {
      setSaving(true);
      await apiClient.put(`/quotes/${quoteId}/sections`, { sections: buildPayload(), tax_rate: taxRate });
      await sendQuote(quoteId);
      await updateQuote(quoteId, { status: 'sent' });
      onSaved();
      onClose();
    } catch {
      setActionError('Failed to send quote.');
    } finally {
      setSaving(false);
    }
  };

  // Section helpers
  const addSection = () => {
    setShowNewSectionInput(true);
  };

  const commitAddSection = () => {
    const name = newSectionName.trim();
    if (!name) return;
    setSections(s => [...s, { id: `s-${Date.now()}`, title: name, items: [] }]);
    setNewSectionName('');
    setShowNewSectionInput(false);
  };

  const deleteSection = (id: string) => {
    setConfirmDeleteSection(id);
  };

  const updateSectionTitle = (id: string, title: string) =>
    setSections(s => s.map(sec => sec.id === id ? { ...sec, title } : sec));

  // Item helpers
  const addItem = (sectionId: string) =>
    setSections(s => s.map(sec => sec.id === sectionId
      ? { ...sec, items: [...sec.items, { id: `i-${Date.now()}`, name: '', description: '', quantity: 1, unitPrice: 0, unit: 'ea' }] }
      : sec));

  const deleteItem = (sectionId: string, itemId: string) =>
    setSections(s => s.map(sec => sec.id === sectionId
      ? { ...sec, items: sec.items.filter(i => i.id !== itemId) }
      : sec));

  const updateItem = (sectionId: string, itemId: string, field: string, value: string | number) =>
    setSections(s => s.map(sec => sec.id === sectionId
      ? { ...sec, items: sec.items.map(i => i.id === itemId ? { ...i, [field]: value } : i) }
      : sec));

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-[#FAF6F0] rounded-xl shadow-2xl w-full max-w-5xl my-4 flex flex-col">
        {/* Hidden Printable Area */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0, backgroundColor: '#ffffff' }}>
          {quoteData && (
            <PrintableQuote 
              ref={printableRef} 
              quote={quoteData} 
              companyInfo={companyInfo} 
            />
          )}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl">
          <div>
            <h2 className="text-xl font-black text-gray-900">
              {loading && !quoteNumber ? 'Creating quote…' : `Quote Builder — ${quoteNumber}`}
            </h2>
            {(jobTitle || clientName) && (
              <p className="text-sm text-gray-500 mt-0.5">
                {jobTitle}{clientName ? ` · ${clientName}` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || !quoteData}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm font-bold hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">print</span>
              Print
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm font-bold hover:bg-gray-50 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              onClick={handleSend}
              disabled={saving || loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">send</span>
              Send Quote
            </button>
            <button onClick={onClose} className="ml-1 p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {actionError && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
            {actionError}
            <button onClick={() => setActionError('')} className="ml-2 text-red-400 hover:text-red-600">
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="flex">
            {/* Sections area */}
            <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
              {sections.map(section => (
                <div key={section.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <input
                      type="text"
                      value={section.title}
                      onChange={e => updateSectionTitle(section.id, e.target.value)}
                      className="text-base font-bold text-gray-900 bg-transparent border-none outline-none flex-1"
                    />
                    <button
                      onClick={() => deleteSection(section.id)}
                      className="ml-2 text-gray-400 hover:text-red-500"
                      title="Delete section"
                    >
                      <span className="material-symbols-outlined text-base">delete</span>
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Item</th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase hidden md:table-cell">Description</th>
                          <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase w-32">Qty / Unit</th>
                          <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase w-24">Unit $</th>
                          <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase w-24">Total</th>
                          <th className="w-8" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {section.items.map(item => (
                          <tr key={item.id}>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={item.name}
                                onChange={e => updateItem(section.id, item.id, 'name', e.target.value)}
                                placeholder="Item name"
                                className="w-full px-2 py-1 rounded border border-gray-200 text-gray-900 text-sm"
                              />
                            </td>
                            <td className="px-3 py-2 hidden md:table-cell">
                              <input
                                type="text"
                                value={item.description}
                                onChange={e => updateItem(section.id, item.id, 'description', e.target.value)}
                                placeholder="Optional"
                                className="w-full px-2 py-1 rounded border border-gray-200 text-gray-500 text-sm"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1 justify-end">
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={e => updateItem(section.id, item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                  className="w-16 px-2 py-1 rounded border border-gray-200 text-right text-sm text-gray-900"
                                />
                                <input
                                  type="text"
                                  value={item.unit}
                                  onChange={e => updateItem(section.id, item.id, 'unit', e.target.value)}
                                  className="w-12 px-2 py-1 rounded border border-gray-200 text-sm text-gray-900"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                step="0.01"
                                value={item.unitPrice}
                                onChange={e => updateItem(section.id, item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 rounded border border-gray-200 text-right text-sm text-gray-900"
                              />
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                              ${(item.quantity * item.unitPrice).toFixed(2)}
                            </td>
                            <td className="px-2">
                              <button
                                onClick={() => deleteItem(section.id, item.id)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <span className="material-symbols-outlined text-base">delete</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                        {section.items.length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-4 py-4 text-center text-gray-400 text-sm">
                              No items — click Add Item below
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
                    <button
                      onClick={() => addItem(section.id)}
                      className="flex items-center gap-1 text-sm text-primary font-medium hover:underline"
                    >
                      <span className="material-symbols-outlined text-base">add</span>
                      Add Item
                    </button>
                  </div>
                </div>
              ))}

              {showNewSectionInput ? (
                <div className="flex items-center gap-2 rounded-xl border-2 border-primary p-3">
                  <input
                    type="text"
                    value={newSectionName}
                    onChange={e => setNewSectionName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') commitAddSection(); if (e.key === 'Escape') { setShowNewSectionInput(false); setNewSectionName(''); } }}
                    autoFocus
                    placeholder="Section name…"
                    className="flex-1 text-sm border-none outline-none bg-transparent"
                  />
                  <button onClick={commitAddSection} className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-lg">Add</button>
                  <button onClick={() => { setShowNewSectionInput(false); setNewSectionName(''); }} className="px-2 py-1 text-gray-500 text-xs hover:text-gray-700">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={addSection}
                  className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 py-4 text-gray-500 hover:border-primary hover:text-primary transition-colors text-sm font-medium"
                >
                  <span className="material-symbols-outlined">add</span>
                  Add Section
                </button>
              )}
            </div>

            {/* Financial Summary sidebar */}
            <div className="w-60 flex-shrink-0 p-6 border-l border-gray-200">
              <div className="sticky top-6 space-y-4">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Summary</h3>

                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Tax&nbsp;rate</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={taxRate}
                        onChange={e => setTaxRate(Number(e.target.value))}
                        className="w-14 px-1 py-0.5 rounded border border-gray-200 text-right text-sm"
                        step="0.1"
                      />
                      <span className="text-gray-400 text-xs">%</span>
                    </div>
                  </div>

                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Tax amount</span>
                    <span>${tax.toFixed(2)}</span>
                  </div>

                  <div className="border-t border-gray-200 pt-3 flex justify-between items-baseline">
                    <span className="font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-black text-primary">${grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="pt-1">
                  <p className="text-xs text-gray-400 mb-1">Status</p>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    status === 'Draft' ? 'bg-gray-100 text-gray-700' :
                    status === 'Sent' ? 'bg-blue-100 text-blue-700' :
                    status === 'Approved' ? 'bg-green-100 text-green-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {confirmDeleteSection !== null && (
        <ConfirmDialog
          title="Delete Section"
          message="Delete this section and all its items? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => {
            setSections(s => s.filter(sec => sec.id !== confirmDeleteSection));
            setConfirmDeleteSection(null);
          }}
          onCancel={() => setConfirmDeleteSection(null)}
        />
      )}

      {confirmSendOpen && (
        <ConfirmDialog
          title="Send Quote"
          message="Send this quote to the client? It will be marked as sent."
          confirmLabel="Send"
          variant="default"
          onConfirm={confirmSend}
          onCancel={() => setConfirmSendOpen(false)}
        />
      )}
    </div>
  );
};
